/*
 * CORE deposit sync. Products with custom.core_deposit_variant must always have exactly one CORE line
 * (properties _core / _core_for) whose quantity equals the total quantity of that parent variant.
 *
 * Every cart change (product page, mini cart, cart page, quick add, other tab, back button, checkout click)
 * runs sync(): read /cart.js and the parent -> CORE variant map rendered by the server, plan the fix,
 * apply it in one /cart/update.js (+ /cart/add.js for missing lines), re-render cart page and mini cart.
 * Theme cart changes publish two events each; overlapping calls are coalesced and the cart is re-checked
 * until it is consistent. Config: window.ATM_CORE_CONFIG = { productCoreVariant, productVariantIds }.
 */
(function () {
  'use strict';

  var CORE_SKU_PREFIX = 'CORE-DEPOSIT';
  var MAX_PASSES = 4;

  function isCoreLine(it) {
    var p = it.properties || {};
    return p._core === 'true' || p._core === true || String(it.sku || '').indexOf(CORE_SKU_PREFIX) === 0;
  }

  // Pure planner (tested in 30_SCRIPTS/test-atm-core-sync.cjs).
  // items: /cart.js items; map: { parentVariantId: coreVariantId } for parents currently in the cart.
  function planCoreSync(items, map) {
    map = map || {};
    var parentQty = {};
    var coreLines = [];
    items.forEach(function (it) {
      if (isCoreLine(it)) { coreLines.push(it); return; }
      var vid = String(it.variant_id);
      if (map[vid]) parentQty[vid] = (parentQty[vid] || 0) + it.quantity;
    });
    var updates = {};
    var kept = {};
    coreLines.forEach(function (it) {
      var forId = String((it.properties && it.properties._core_for) || '');
      var want = parentQty[forId] || 0;
      var rightVariant = !!map[forId] && String(map[forId]) === String(it.variant_id);
      if (!want || !rightVariant || kept[forId]) {
        // orphan, wrong deposit variant, deposit added on its own, or a duplicate line for the same parent
        updates[it.key] = 0;
        return;
      }
      kept[forId] = true;
      if (it.quantity !== want) updates[it.key] = want;
    });
    var adds = [];
    Object.keys(parentQty).forEach(function (vid) {
      if (parentQty[vid] > 0 && !kept[vid]) {
        adds.push({ id: Number(map[vid]), quantity: parentQty[vid], properties: { _core: 'true', _core_for: vid } });
      }
    });
    return { updates: updates, adds: adds, changed: Object.keys(updates).length > 0 || adds.length > 0 };
  }

  var api = { planCoreSync: planCoreSync, isCoreLine: isCoreLine };
  if (typeof window !== 'undefined') window.ATMCoreSync = api;
  if (typeof document === 'undefined' || typeof window === 'undefined' || !window.fetch) return;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function route(name, fallback) {
    return (window.routes && window.routes[name]) || root() + fallback;
  }
  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  // null = map unknown (request failed) -> never remove deposits on a guess
  function fetchMap() {
    return getJSON(root() + '?sections=header-mini-cart-content&v=' + Date.now())
      .then(function (data) {
        var html = data['header-mini-cart-content'] || '';
        var el = new DOMParser().parseFromString(html, 'text/html').getElementById('atm-core-variant-map');
        if (!el) return {};
        return JSON.parse(el.textContent || '{}');
      })
      .catch(function () { return null; });
  }

  function setBusy(on) {
    ['#main-cart-items', 'mini-cart .mini-cart-inner'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (!el) return;
      el.classList.toggle('atm-core-syncing', on);
      if (on) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy');
    });
  }

  var coreHandles = {};
  function lockCoreLines() {
    // Fallback only: CORE rows are rendered server-side without quantity input / remove button.
    document.querySelectorAll('tr.cart-item, .cart-item, .mini-cart-item').forEach(function (row) {
      var link = row.querySelector('.cart-item-name, a[href*="/products/"]');
      var href = (link && link.getAttribute('href')) || '';
      var matched = Object.keys(coreHandles).some(function (h) { return href.indexOf('/products/' + h) !== -1; });
      if (!matched && !row.classList.contains('atm-core-line')) return;
      row.classList.add('atm-core-line');
      row.querySelectorAll('.quantity-input-button, input[name="updates[]"], .quantity-input-field').forEach(function (el) {
        el.setAttribute('disabled', 'disabled');
      });
      var rm = row.querySelector('cart-remove-button, .button-cart-remove, .mini-cart-item-remove');
      if (rm) rm.style.display = 'none';
    });
  }

  function refreshUI(cart) {
    var onCartPage = !!document.getElementById('main-cart-items');
    if (onCartPage && cart && cart.item_count === 0) {
      // empty-cart state is rendered by the page template, not by the section refresh
      window.location.reload();
      return Promise.resolve();
    }
    var tasks = [];
    var main = document.querySelector('cart-items');
    if (main && typeof main.updateSectionContents === 'function') tasks.push(main.updateSectionContents());
    if (window.miniCart && typeof window.miniCart.updateSectionContents === 'function') {
      tasks.push(window.miniCart.updateSectionContents());
    }
    return Promise.all(tasks.map(function (p) { return Promise.resolve(p).catch(function () {}); }));
  }

  // One pass: returns the cart after the fix, or null when nothing had to change.
  function pass() {
    return getJSON(root() + 'cart.js').then(function (cart) {
      coreHandles = {};
      cart.items.forEach(function (it) { if (isCoreLine(it)) coreHandles[it.handle] = true; });
      if (!cart.items.length) return null;
      return fetchMap().then(function (map) {
        if (map === null) return null;
        var plan = planCoreSync(cart.items, map);
        if (!plan.changed) return null;
        setBusy(true);
        var step = Object.keys(plan.updates).length
          ? postJSON(route('cart_update_url', 'cart/update'), { updates: plan.updates })
          : Promise.resolve(null);
        return step.then(function (updated) {
          if (!plan.adds.length) return updated;
          return postJSON(route('cart_add_url', 'cart/add'), { items: plan.adds }).then(function () {
            return getJSON(root() + 'cart.js');
          });
        });
      });
    });
  }

  var running = null;
  var again = false;

  function sync() {
    if (running) { again = true; return running; }
    var lastCart = null;
    var inner = (function loop(n) {
      again = false;
      return pass().then(function (cart) {
        if (cart) lastCart = cart;
        // after a fix always verify once more; also re-run when the customer changed the cart meanwhile
        if ((cart || again) && n + 1 < MAX_PASSES) return loop(n + 1);
      });
    })(0)
      .then(function () { return lastCart ? refreshUI(lastCart) : null; })
      .catch(function (e) { if (window.console) console.error('CORE sync failed', e); })
      .then(function () {
        setBusy(false);
        lockCoreLines();
        return !!lastCart;
      });
    running = inner.then(function (changed) {
      running = null;
      if (again) return sync().then(function (c) { return c || changed; });
      return changed;
    });
    return running;
  }

  // Only forms that add a variant of THIS product get its deposit (recommendations / upsells on the same
  // page are other products: their deposit, if any, comes from the cart sync via the server map).
  function hookProductForm(coreVariant, variantIds) {
    if (!coreVariant) return;
    var own = {};
    (variantIds || []).forEach(function (id) { own[String(id)] = true; });
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      if (form.__coreHooked) return;
      form.__coreHooked = true;
      form.addEventListener('submit', function (e) {
        var idEl = form.querySelector('[name="id"]');
        if (!idEl || !own[String(idEl.value)]) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        form.dataset.atmCoreVariant = String(coreVariant);
        // Binding UX (AGENTS.md, Add to cart): stay on the product + mini cart, never redirect to /cart.
        if (window.ATMCart) window.ATMCart.addForm(form, e.submitter);
        else form.submit(); // the cart page sync then adds the deposit
      }, true);
    });
  }

  function hookCheckoutButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('button[name="checkout"]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      btn.disabled = true;
      // The cart-page button sits outside its form (form="cart"), and form.submit()
      // drops the submitter's name="checkout", so Shopify would only update the cart.
      // Fail open: whatever happens with the CORE sync, the customer reaches checkout.
      function goToCheckout() {
        var form = btn.form || btn.closest('form');
        btn.disabled = false;
        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit(btn);
        } else if (form) {
          var hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'checkout';
          form.appendChild(hidden);
          form.submit();
        } else {
          window.location.href = root() + 'checkout';
        }
      }
      sync().then(goToCheckout, goToCheckout);
    }, true);
  }

  function hookCartEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    [PUB_SUB_EVENTS.cartUpdate, PUB_SUB_EVENTS.cartItemRemove, PUB_SUB_EVENTS.cartItemQuantityUpdate, PUB_SUB_EVENTS.cartItemAdd]
      .forEach(function (ev) { subscribe(ev, function () { sync(); }); });
    // back/forward cache and changes made in another tab
    window.addEventListener('pageshow', function (e) { if (e.persisted) sync(); });
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') sync(); });
  }

  function fixCertBeforeHazmat() {
    document.querySelectorAll('.product-info').forEach(function (info) {
      if (info.querySelector('.atm-commerce-extras')) return;
      var haz = info.querySelector('.atm-notice--hazmat');
      var cert = info.querySelector('.atm-notice--cert');
      if (!haz || !cert) return;
      var hazBlock = haz.closest('.product-block');
      var certBlock = cert.closest('.product-block');
      if (!hazBlock || !certBlock || hazBlock === certBlock) return;
      if (hazBlock.compareDocumentPosition(certBlock) & Node.DOCUMENT_POSITION_FOLLOWING) {
        hazBlock.before(certBlock);
      }
    });
  }

  function initProductCommerceOrder() {
    if (!document.body.classList.contains('template-product')) return;
    fixCertBeforeHazmat();
    if (typeof subscribe === 'function') subscribe('product:variant-change', fixCertBeforeHazmat);
  }

  function init() {
    var cfg = window.ATM_CORE_CONFIG || {};
    hookProductForm(cfg.productCoreVariant, cfg.productVariantIds);
    hookCheckoutButtons();
    hookCartEvents();
    initProductCommerceOrder();
    sync();
  }

  api.sync = sync;
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
